/**
 * Dashboard tab on the SuperAdmin Platform page.
 *
 * Platform-wide time-series for Sections A-E (Users & Growth, Trading
 * Activity, Platform P&L, Capital, Broker Health). Backed by
 * GET /analytics/platform-dashboard (SuperAdmin only), which reads PLATFORM
 * sentinel rows stamped nightly by the analytics aggregator at 16:10 IST.
 *
 * Conventions:
 *  - Snapshot fields (total users, broker accounts) reflect CURRENT state
 *    at the latest aggregator run, not the historical date. The "Snapshot
 *    as of …" caveat under those KPIs makes that explicit.
 *  - Time-series fields (pnl, trades, etc.) are accurate for the date.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ResponsiveContainer,
  ComposedChart, BarChart, AreaChart, PieChart, Pie,
  Bar, Area, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine,
} from 'recharts';
import { AlertCircle, Download, RefreshCw } from 'lucide-react';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Button from '../../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import platformPnlService from '../../services/platformPnlService';
import { downloadFile } from '../../utils/downloadFile';
import { cn } from '../../utils/cn';
import { exitTypeLabel } from '../../utils/exitTypeLabel';
import { formatInr, formatPct, istCalendarToday } from '../../utils/platformFormat';
import type { PlatformDashboardResponse } from '../../types/analytics';

// ─── range presets ──────────────────────────────────────────────────────

type RangePreset = '7d' | '30d' | '90d' | '180d' | '1y' | 'all';

interface RangeSpec { label: string; days: number | 'all'; }
const RANGES: Record<RangePreset, RangeSpec> = {
  '7d':   { label: '7d',   days: 7 },
  '30d':  { label: '30d',  days: 30 },
  '90d':  { label: '90d',  days: 90 },
  '180d': { label: '180d', days: 180 },
  '1y':   { label: '1y',   days: 365 },
  'all':  { label: 'All',  days: 'all' },
};
const DEFAULT_RANGE: RangePreset = '30d';

function deriveRange(preset: RangePreset): { start: string; end: string } {
  const end = istCalendarToday();
  if (preset === 'all') {
    // Far past — backend will clamp to whatever rows exist.
    return { start: '2020-01-01', end };
  }
  const days = RANGES[preset].days as number;
  const [y, m, d] = end.split('-').map(Number);
  const cursor = new Date(Date.UTC(y, m - 1, d));
  cursor.setUTCDate(cursor.getUTCDate() - days + 1);
  return { start: cursor.toISOString().slice(0, 10), end };
}

// ─── tab ────────────────────────────────────────────────────────────────

interface DashboardTabProps {
  registerActions: (node: React.ReactNode) => void;
}

const DashboardTab: React.FC<DashboardTabProps> = ({ registerActions }) => {
  const [range, setRange] = useState<RangePreset>(DEFAULT_RANGE);
  const [data, setData] = useState<PlatformDashboardResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPage = useCallback(async (preset: RangePreset) => {
    const { start, end } = deriveRange(preset);
    setIsLoading(true);
    setError(null);
    try {
      const resp = await platformPnlService.getPlatformDashboard(start, end);
      setData(resp);
    } catch (err: any) {
      setError(err?.message || 'Failed to load platform dashboard');
      setData(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { fetchPage(range); }, [range, fetchPage]);

  // Memoize so the `[]` fallback doesn't generate a fresh array each render
  // and re-trigger every downstream useMemo's exhaustive-deps check.
  const series = useMemo(() => data?.series || [], [data]);
  const summary = data?.summary;
  const snapshot = data?.latest_snapshot;

  // ── derived series for charts ──
  const pnlSeries = useMemo(() => series.map((r) => ({
    date: r.date.slice(5),       // MM-DD
    pnl: r.net_pnl,
  })), [series]);

  // Cumulative equity = running sum of net_pnl across the window.
  const equitySeries = useMemo(() => {
    let running = 0;
    return series.map((r) => {
      running += r.net_pnl;
      return { date: r.date.slice(5), equity: Math.round(running * 100) / 100 };
    });
  }, [series]);

  const tradesSeries = useMemo(() => series.map((r) => ({
    date: r.date.slice(5),
    trades: r.trades_total,
    won: r.trades_won,
    lost: r.trades_lost,
  })), [series]);

  const usersTradedSeries = useMemo(() => series.map((r) => ({
    date: r.date.slice(5),
    traded: r.users_traded,
    profitable: r.users_profitable,
    lossMaking: r.users_loss_making,
  })), [series]);

  const signupsSeries = useMemo(() => series.map((r) => ({
    date: r.date.slice(5),
    new_users: r.users_new_today,
  })), [series]);

  // ── pie data ──
  const brokerPie = useMemo(() => {
    const dist = snapshot?.broker_distribution || {};
    return Object.entries(dist)
      .map(([broker, count]) => ({ name: broker.toUpperCase(), value: count }))
      .sort((a, b) => b.value - a.value);
  }, [snapshot]);

  const underlyingPie = useMemo(() => {
    // Sum across the whole window.
    const totals: Record<string, number> = {};
    for (const r of series) {
      for (const [u, val] of Object.entries(r.underlying_breakdown || {})) {
        totals[u] = (totals[u] || 0) + (val?.count || 0);
      }
    }
    return Object.entries(totals)
      .map(([u, count]) => ({ name: u, value: count }))
      .sort((a, b) => b.value - a.value);
  }, [series]);

  const exitReasonPie = useMemo(() => {
    const totals: Record<string, number> = {};
    for (const r of series) {
      for (const [k, val] of Object.entries(r.exit_reason_breakdown || {})) {
        totals[k] = (totals[k] || 0) + (val?.count || 0);
      }
    }
    return Object.entries(totals)
      .map(([k, count]) => ({ name: exitTypeLabel(k) ?? k, value: count }))
      .sort((a, b) => b.value - a.value);
  }, [series]);

  const autoLoginPct = snapshot && snapshot.broker_accounts_total > 0
    ? (snapshot.auto_login_enabled_count / snapshot.broker_accounts_total) * 100
    : null;

  const onExportCsv = useCallback(() => {
    const header = [
      'date',
      'users_traded', 'users_profitable', 'users_loss_making',
      'net_pnl', 'trades_total', 'trades_won', 'trades_lost',
      'capital_deployed_traded', 'best_user_pnl', 'worst_user_pnl',
      'users_new_today', 'users_total_snapshot', 'users_active_snapshot',
    ];
    const escape = (v: any): string => {
      let s = v == null ? '' : typeof v === 'object' ? JSON.stringify(v) : String(v);
      if (s && /^[=+\-@]/.test(s)) s = `'${s}`;
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [
      header.join(','),
      ...series.map((r) => [
        r.date, r.users_traded, r.users_profitable, r.users_loss_making,
        r.net_pnl, r.trades_total, r.trades_won, r.trades_lost,
        r.capital_deployed_traded, r.best_user_pnl, r.worst_user_pnl,
        r.users_new_today, r.users_total, r.users_active,
      ].map(escape).join(',')),
    ];
    downloadFile(
      `platform-dashboard-${data?.start || istCalendarToday()}-to-${data?.end || istCalendarToday()}.csv`,
      lines.join('\n'),
      'text/csv;charset=utf-8',
    );
  }, [series, data]);

  useEffect(() => {
    registerActions(
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => fetchPage(range)}
          leftIcon={<RefreshCw className={cn('h-4 w-4', isLoading && 'animate-spin')} />}
          disabled={isLoading}
        >
          Refresh
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={onExportCsv}
          leftIcon={<Download className="h-4 w-4" />}
          disabled={series.length === 0}
        >
          Export CSV
        </Button>
      </div>
    );
  }, [registerActions, fetchPage, range, isLoading, onExportCsv, series.length]);

  return (
    <div className="space-y-4">
      {/* Range chips */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
          Range
        </span>
        {(Object.keys(RANGES) as RangePreset[]).map((k) => (
          <button
            key={k}
            type="button"
            onClick={() => setRange(k)}
            className={cn(
              'px-2.5 py-1 text-xs rounded-md border transition-colors',
              range === k
                ? 'bg-blue-50 dark:bg-blue-900/30 border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-200'
                : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700',
            )}
          >
            {RANGES[k].label}
          </button>
        ))}
        {snapshot?.computed_at && (
          <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
            Snapshot fields as of {snapshot.computed_at.slice(0, 16).replace('T', ' ')} IST
          </span>
        )}
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <span className="text-red-800 dark:text-red-200 text-sm">{error}</span>
        </div>
      )}

      {/* Top KPI strip — range totals */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <Kpi label={`Net P&L · ${RANGES[range].label}`}
             value={formatInr(summary?.total_net_pnl)}
             tone={(summary?.total_net_pnl ?? 0) >= 0 ? 'pos' : 'neg'} />
        <Kpi label={`Trades · ${RANGES[range].label}`}
             value={String(summary?.total_trades ?? 0)} tone="neutral" />
        <Kpi label="Win rate" value={formatPct(summary?.win_rate, 1)} tone="neutral" />
        <Kpi label="Best day" value={formatInr(summary?.best_day_pnl)} tone="pos" />
        <Kpi label="Worst day" value={formatInr(summary?.worst_day_pnl)} tone="neg" />
        <Kpi label="Peak traders/day" value={String(summary?.max_users_traded ?? 0)} tone="neutral" />
      </div>

      {/* Snapshot KPI strip (right-now state) */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <Kpi label="Total users" value={String(snapshot?.users_total ?? 0)} tone="neutral" />
        <Kpi label="Active users" value={String(snapshot?.users_active ?? 0)} tone="neutral" />
        <Kpi label="Trading enabled" value={String(snapshot?.users_trading_enabled ?? 0)} tone="neutral" />
        <Kpi label="Broker accounts" value={String(snapshot?.broker_accounts_total ?? 0)} tone="neutral" />
        <Kpi label="Auto-login" value={formatPct(autoLoginPct, 0)} tone={(autoLoginPct ?? 0) >= 80 ? 'pos' : 'neg'} />
        <Kpi label="Total capital" value={formatInr(snapshot?.total_broker_capital)} tone="neutral" />
      </div>

      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner />
          <span className="ml-2 text-gray-600 dark:text-gray-400">Loading…</span>
        </div>
      )}

      {!isLoading && series.length === 0 && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
          <div className="flex items-center gap-2 text-yellow-800 dark:text-yellow-200">
            <AlertCircle className="h-5 w-5" />
            <span className="text-sm">
              No PLATFORM rows for this range. If the aggregator was just deployed,
              run <code className="px-1 rounded bg-yellow-100 dark:bg-yellow-900/40">scripts/backfill_platform_dashboard.py</code> to
              populate history; otherwise wait for tonight's 16:10 IST run.
            </span>
          </div>
        </div>
      )}

      {/* ─── Section A: Users & Growth ─── */}
      {!isLoading && series.length > 0 && (
        <>
          <SectionHeading>Users & Growth</SectionHeading>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <ChartCard title="Daily new signups">
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={signupsSeries}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.18} />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} minTickGap={32} />
                  <YAxis tick={{ fontSize: 11 }} width={32} allowDecimals={false} />
                  <Tooltip contentStyle={{ borderRadius: 6, fontSize: 12 }} />
                  <Bar dataKey="new_users" name="New users" fill="#3b82f6" />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Active traders per day">
              <ResponsiveContainer width="100%" height={240}>
                <ComposedChart data={usersTradedSeries}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.18} />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} minTickGap={32} />
                  <YAxis tick={{ fontSize: 11 }} width={32} allowDecimals={false} />
                  <Tooltip contentStyle={{ borderRadius: 6, fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="profitable" stackId="x" name="Profitable" fill="#10b981" />
                  <Bar dataKey="lossMaking" stackId="x" name="Loss-making" fill="#ef4444" />
                </ComposedChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>

          {/* ─── Section B: Trading Activity ─── */}
          <SectionHeading>Trading Activity</SectionHeading>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <ChartCard title="Daily trade count">
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={tradesSeries}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.18} />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} minTickGap={32} />
                  <YAxis tick={{ fontSize: 11 }} width={40} allowDecimals={false} />
                  <Tooltip contentStyle={{ borderRadius: 6, fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="won" stackId="t" name="Won" fill="#10b981" />
                  <Bar dataKey="lost" stackId="t" name="Lost" fill="#ef4444" />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Trades by underlying (range total)">
              {underlyingPie.length === 0 ? <EmptyChart /> : (
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                    <Pie data={underlyingPie} dataKey="value" nameKey="name"
                         outerRadius={80} label={(d: any) => d.name}>
                      {underlyingPie.map((_, i) => (
                        <Cell key={i} fill={PIE_PALETTE[i % PIE_PALETTE.length]} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ borderRadius: 6, fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </ChartCard>
          </div>

          {/* ─── Section C: Platform P&L ─── */}
          <SectionHeading>Platform P&amp;L</SectionHeading>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <ChartCard title="Daily platform P&L">
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={pnlSeries}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.18} />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} minTickGap={32} />
                  <YAxis tick={{ fontSize: 11 }} width={70}
                         tickFormatter={(v) => `₹${Math.round(Number(v)).toLocaleString('en-IN')}`} />
                  <Tooltip
                    formatter={(v: any) => `₹${Math.round(Number(v)).toLocaleString('en-IN')}`}
                    contentStyle={{ borderRadius: 6, fontSize: 12 }}
                  />
                  <ReferenceLine y={0} stroke="#9ca3af" />
                  <Bar dataKey="pnl" name="Net P&L">
                    {pnlSeries.map((p, i) => (
                      <Cell key={i} fill={p.pnl >= 0 ? '#10b981' : '#ef4444'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Cumulative equity">
              <ResponsiveContainer width="100%" height={240}>
                <AreaChart data={equitySeries}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.18} />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} minTickGap={32} />
                  <YAxis tick={{ fontSize: 11 }} width={70}
                         tickFormatter={(v) => `₹${Math.round(Number(v)).toLocaleString('en-IN')}`} />
                  <Tooltip
                    formatter={(v: any) => `₹${Math.round(Number(v)).toLocaleString('en-IN')}`}
                    contentStyle={{ borderRadius: 6, fontSize: 12 }}
                  />
                  <ReferenceLine y={0} stroke="#9ca3af" />
                  <Area type="monotone" dataKey="equity" name="Cumulative P&L"
                        stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.2} />
                </AreaChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>

          <ChartCard title="Trades by exit reason (range total)">
            {exitReasonPie.length === 0 ? <EmptyChart /> : (
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie data={exitReasonPie} dataKey="value" nameKey="name"
                       outerRadius={80} label={(d: any) => `${d.name} (${d.value})`}>
                    {exitReasonPie.map((_, i) => (
                      <Cell key={i} fill={PIE_PALETTE[i % PIE_PALETTE.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ borderRadius: 6, fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </ChartCard>

          {/* ─── Section D: Capital ─── */}
          <SectionHeading>Capital</SectionHeading>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <ChartCard title="Capital distribution across users (snapshot)">
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={snapshot?.capital_distribution || []}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.18} />
                  <XAxis dataKey="bucket" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 11 }} width={32} allowDecimals={false} />
                  <Tooltip contentStyle={{ borderRadius: 6, fontSize: 12 }} />
                  <Bar dataKey="count" name="Accounts" fill="#3b82f6" />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <Card>
              <CardHeader>
                <CardTitle>Capital summary</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm">
                  <RowLine label="Total capital across accounts"
                           value={formatInr(snapshot?.total_broker_capital)} />
                  <RowLine label="Capital deployed today (traded only)"
                           value={formatInr(snapshot?.capital_deployed_traded ?? 0)} />
                  <RowLine label="Broker accounts" value={String(snapshot?.broker_accounts_total ?? 0)} />
                  <RowLine label="Avg capital per account"
                           value={
                             snapshot && snapshot.broker_accounts_total > 0
                               ? formatInr(snapshot.total_broker_capital / snapshot.broker_accounts_total)
                               : '—'
                           } />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* ─── Section E: Broker Health ─── */}
          <SectionHeading>Broker Health</SectionHeading>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <ChartCard title="Broker distribution (snapshot)">
              {brokerPie.length === 0 ? <EmptyChart /> : (
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                    <Pie data={brokerPie} dataKey="value" nameKey="name"
                         outerRadius={80} label={(d: any) => `${d.name} (${d.value})`}>
                      {brokerPie.map((_, i) => (
                        <Cell key={i} fill={PIE_PALETTE[i % PIE_PALETTE.length]} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ borderRadius: 6, fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </ChartCard>

            <ChartCard title="Auto-login enabled per broker">
              {brokerPie.length === 0 ? <EmptyChart /> : (
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart
                    data={brokerPie.map((b) => {
                      const total = b.value;
                      const enabled = snapshot?.auto_login_per_broker?.[b.name.toLowerCase()] || 0;
                      const pct = total > 0 ? (enabled / total) * 100 : 0;
                      return { name: b.name, enabled, total, pct: Number(pct.toFixed(1)) };
                    })}
                  >
                    <CartesianGrid strokeDasharray="3 3" opacity={0.18} />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} width={32}
                           domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                    <Tooltip
                      formatter={(v: any, name: any) => name === 'Auto-login %'
                        ? `${Number(v).toFixed(1)}%` : v}
                      contentStyle={{ borderRadius: 6, fontSize: 12 }}
                    />
                    <Bar dataKey="pct" name="Auto-login %" fill="#10b981" />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </ChartCard>
          </div>
        </>
      )}
    </div>
  );
};

// ─── child components ────────────────────────────────────────────────────

const PIE_PALETTE = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6',
                     '#06b6d4', '#ec4899', '#84cc16', '#f97316'];

interface KpiProps {
  label: string;
  value: string;
  tone: 'pos' | 'neg' | 'neutral';
}

const Kpi: React.FC<KpiProps> = ({ label, value, tone }) => {
  const toneClass = tone === 'pos'
    ? 'text-green-600 dark:text-green-400'
    : tone === 'neg'
      ? 'text-red-600 dark:text-red-400'
      : 'text-gray-900 dark:text-white';
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
      <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{label}</div>
      <div className={cn('text-xl font-bold mt-1 tabular-nums', toneClass)}>{value}</div>
    </div>
  );
};

const SectionHeading: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h2 className="text-lg font-semibold text-gray-900 dark:text-white pt-2">{children}</h2>
);

const ChartCard: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <Card>
    <CardHeader>
      <CardTitle>{title}</CardTitle>
    </CardHeader>
    <CardContent>{children}</CardContent>
  </Card>
);

const EmptyChart: React.FC = () => (
  <div className="h-[240px] flex items-center justify-center text-sm text-gray-400 dark:text-gray-500">
    No data
  </div>
);

const RowLine: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="flex justify-between border-b border-gray-100 dark:border-gray-700 pb-1.5">
    <span className="text-gray-600 dark:text-gray-300">{label}</span>
    <span className="font-medium text-gray-900 dark:text-white tabular-nums">{value}</span>
  </div>
);

export default DashboardTab;
